/**
 * src/components/layout/Header.tsx (Professional Design)
 *
 * Replace your existing header with this professional version that shows
 * the user's actual name and matches the design.
 */

import React, {useState} from 'react';
import {useAuth} from '@/context/AuthContext';
import {ChevronDown, LogOut, User} from 'lucide-react';

export const Header: React.FC = () => {
    const {user, signOut} = useAuth();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleSignOut = async () => {
        if (isLoggingOut) return; // Prevent multiple clicks

        console.log('Header logout clicked');
        setIsLoggingOut(true);
        setIsDropdownOpen(false);

        try {
            await signOut();
        } catch (error) {
            console.error('Sign out error:', error);
            window.location.href = '/login';
        }
    };

    // Extract first name from full name or email
    const getDisplayName = () => {
        if (!user) return 'User';

        if (user.full_name) {
            // Extract first name from full name
            const firstName = user.full_name.split(' ')[0];
            return firstName;
        }

        // Fallback to email username
        return user.email.split('@')[0];
    };

    return (
        <header className="fixed top-0 right-0 left-64 z-40 bg-white border-b border-gray-200 h-16">
            <div className="flex items-center justify-between h-full px-6">
                {/* Page Title Area - can be dynamic */}
                <div className="flex items-center">

                </div>

                {/* User Profile Dropdown */}
                <div className="relative">
                    <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="flex items-center space-x-3 text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded-md px-3 py-2"
                    >
                        {/* User Avatar */}
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-300 rounded-full">
                            <User className="w-4 h-4 text-gray-600"/>
                        </div>

                        {/* User Name */}
                        <span className="text-sm font-medium">{getDisplayName()}</span>

                        {/* Dropdown Arrow */}
                        <ChevronDown className="w-4 h-4"/>
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                        <div
                            className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                            {/* User Info */}
                            <div className="px-4 py-2 border-b border-gray-100">
                                <p className="text-sm font-medium text-gray-900">{user?.full_name || 'System Administrator'}</p>
                                <p className="text-xs text-gray-500">{user?.email}</p>
                                <p className="text-xs text-orange-600 capitalize">{user?.role.replace('_', ' ')}</p>
                            </div>

                            {/* Menu Items */}
                            <button
                                onClick={handleSignOut}
                                disabled={isLoggingOut}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                <LogOut className="w-4 h-4 mr-3"/>
                                {isLoggingOut ? 'Signing Out...' : 'Sign Out'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Click outside to close dropdown */}
            {isDropdownOpen && (
                <div
                    className="fixed inset-0 z-30"
                    onClick={() => setIsDropdownOpen(false)}
                />
            )}
        </header>
    );
};

export default Header;